import React from "react";
import { hasFeature, resolveImageUrl } from "../../services/api";
import { DIETARY_TAG_OPTIONS } from "./dietaryTags";
import { resizeImage } from "./resizeImage";
import { useT } from "../../i18n/useT";

// AM/OM are machine drafts (2026-06) — pending native review
const STRINGS = {
  en: {
    editItem: "Edit item",
    addItem: "Add item",
    nameEn: "Name (English)",
    nameAm: "Name (Amharic / አማርኛ)",
    nameOm: "Name (Afaan Oromo)",
    nameSid: "Name (Sidaamu Afoo / Af Sidama)",
    nameTi: "Name (ት ግር ኛ / Tigrinya)",
    nameNl: "Naam (Nederlands)",
    optionalFallback: "(optional — falls back to English if blank)",
    category: "Category",
    categoryPlaceholder: "Food, Beverage, Dessert...",
    station: "Station", station_auto: "Auto (suggest)",
    price: "Price",
    cost: "Cost (optional)",
    costHint: "Your cost to make it — unlocks profit analysis",
    description: "Description",
    photo: "Photo",
    changePhoto: "Change photo",
    choosePhoto: "Choose photo",
    remove: "Remove",
    photoHint:
      "JPG, PNG, or WebP. Big photos from your phone are automatically resized for fast loading.",
    recipe: "Recipe / ingredients (one per line)",
    modifiers: 'Modifiers (one per line — "Name" or "Name|Price")',
    dietaryTags: "Dietary & allergen tags",
    dietaryHint:
      "Guests filter by these on the menu page — be accurate, this is an allergen / dietary signal.",
    available: "Available",
    chefPick: '⭐ Chef\'s pick (show in "Recommended" strip)',
    caption: "Caption (optional, shown under the item)",
    captionPlaceholder: "e.g. Chef's specialty for Friday nights",
    errImage: "Could not load that image.",
    save: "Save",
    add: "Add",
    cancel: "Cancel",
  },
  am: {
    editItem: "ዕቃ አርትዕ",
    addItem: "ዕቃ ጨምር",
    nameEn: "ስም (እንግሊዝኛ)",
    nameAm: "ስም (አማርኛ)",
    nameOm: "ስም (አፋን ኦሮሞ)",
    nameSid: "ስም (ሲዳምኛ / አፍ ሲዳማ)",
    nameTi: "ስም (ትግርኛ)",
    nameNl: "ስም (ደች)",
    optionalFallback: "(አማራጭ — ባዶ ከሆነ ወደ እንግሊዝኛ ይመለሳል)",
    category: "ምድብ",
    categoryPlaceholder: "ምግብ፣ መጠጥ፣ ጣፋጭ...",
    station: "ጣቢያ", station_auto: "በራስ-ሰር (ጠቁም)",
    price: "ዋጋ",
    cost: "ወጪ (አማራጭ)",
    costHint: "ለማዘጋጀት የሚያስወጣዎት — የትርፍ ትንተና ይከፍታል",
    description: "መግለጫ",
    photo: "ምስል",
    changePhoto: "ምስል ቀይር",
    choosePhoto: "ምስል ምረጥ",
    remove: "አጥፋ",
    photoHint:
      "JPG፣ PNG፣ ወይም WebP። ከስልክዎ ያሉ ትላልቅ ምስሎች ለፈጣን ጭነት በራስ-ሰር ይስተካከላሉ።",
    recipe: "የምግብ አሰራር / ግብዓቶች (በመስመር አንድ)",
    modifiers: 'ማሻሻያዎች (በመስመር አንድ — "ስም" ወይም "ስም|ዋጋ")',
    dietaryTags: "የምግብ እና አለርጂ ምልክቶች",
    dietaryHint:
      "እንግዶች በሜኑ ገጽ ላይ በእነዚህ ያጣራሉ — ትክክለኛ ይሁኑ፣ ይህ የአለርጂ / የምግብ ምልክት ነው።",
    available: "ይገኛል",
    chefPick: '⭐ የሼፍ ምርጫ (በ"የተመከረ" ሰቅ አሳይ)',
    caption: "መግለጫ (አማራጭ፣ ከዕቃው ስር የሚታይ)",
    captionPlaceholder: "ለምሳሌ፡ ለዓርብ ምሽቶች የሼፍ ልዩ ምግብ",
    errImage: "ያን ምስል መጫን አልተቻለም።",
    save: "አስቀምጥ",
    add: "ጨምር",
    cancel: "ሰርዝ",
  },
  om: {
    editItem: "Meeshaa Sirreessi",
    addItem: "Meeshaa Dabali",
    nameEn: "Maqaa (Ingiliffa)",
    nameAm: "Maqaa (Amaariffa / አማርኛ)",
    nameOm: "Maqaa (Afaan Oromoo)",
    nameSid: "Maqaa (Sidaamu Afoo / Af Sidama)",
    nameTi: "Maqaa (Tigreffa / ት ግር ኛ)",
    nameNl: "Maqaa (Holaandiffa)",
    optionalFallback: "(filannoo — yoo duwwaa ta'e gara Ingiliffaatti deebi'a)",
    category: "Ramaddii",
    categoryPlaceholder: "Nyaata, Dhugaatii, Mi'aawaa...",
    station: "Iddoo", station_auto: "Ofumaan (yaada)",
    price: "Gatii",
    cost: "Baasii (filannoo)",
    costHint: "Baasii qophii kee — xiinxala bu'aa banaa",
    description: "Ibsa",
    photo: "Suuraa",
    changePhoto: "Suuraa Jijjiiri",
    choosePhoto: "Suuraa Filadhu",
    remove: "Balleessi",
    photoHint:
      "JPG, PNG, yookiin WebP. Suuraaleen gurguddoo bilbila kee irraa fe'umsa saffisaaf ofumaan guddinni isaanii sirreeffama.",
    recipe: "Qophii / wantoota (sarara tokkoon tokko)",
    modifiers: 'Jijjiirtota (sarara tokkoon tokko — "Maqaa" yookiin "Maqaa|Gatii")',
    dietaryTags: "Mallattoo Nyaataa fi Alarjii",
    dietaryHint:
      "Keessummoonni fuula meenuu irratti kanneeniin gargari baasu — sirrii ta'i, kun mallattoo alarjii / nyaataa ti.",
    available: "Ni argama",
    chefPick: '⭐ Filannoo Cheef (sarara "Yaadame" keessatti agarsiisi)',
    caption: "Ibsa (filannoo, meeshaa jala mul'atu)",
    captionPlaceholder: "fkn. Nyaata addaa Cheef halkan Jimaataa",
    errImage: "Suuraa sana fe'uun hin danda'amne.",
    save: "Olkaa'i",
    add: "Dabali",
    cancel: "Dhiisi",
  },
};

// Add / edit menu item form. Purely presentational — all state, refs
// and submit/reset handlers live in ManagerMenuPage and arrive as props.
export default function MenuItemForm({
  form,
  setForm,
  editingId,
  onSubmit,
  onCancel,
  pendingImage,
  setPendingImage,
  removeExistingImage,
  setRemoveExistingImage,
  formImageInputRef,
  setError,
  showAm,
  showStation,
  showOm,
  showSid,
  showTi,
  showNl,
}) {
  const { lang } = useT();
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;
  return (
    <form className="card" onSubmit={onSubmit}>
      <h3 style={{ marginTop: 0 }}>{editingId ? t("editItem") : t("addItem")}</h3>
      <div className="field">
        <label>{t("nameEn")}</label>
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />
      </div>
      {/* Secondary-language name fields are gated by the
          admin-controlled supported_languages list (see
          TenantSettingsPage). An Addis hotel set to EN+AM sees only
          the Amharic input; Sintalle Hawassa set to EN+AM+SID sees
          Amharic and Af Sidama; an Eindhoven diaspora set to
          EN+NL+AM sees Dutch and Amharic. Tenants with no override
          fall back to country defaults. */}
      {showAm && (
        <div className="field">
          <label>{t("nameAm")}</label>
          <input
            lang="am"
            value={form.name_am}
            onChange={(e) => setForm({ ...form, name_am: e.target.value })}
            placeholder="ለምሳሌ: ቡና"
          />
        </div>
      )}
      {showOm && (
        <div className="field">
          <label>{t("nameOm")}</label>
          <input
            lang="om"
            value={form.name_om}
            onChange={(e) => setForm({ ...form, name_om: e.target.value })}
            placeholder="fkn: Bunaa"
          />
        </div>
      )}
      {showSid && (
        <div className="field">
          <label>{t("nameSid")}</label>
          <input
            lang="sid"
            value={form.name_sid}
            onChange={(e) => setForm({ ...form, name_sid: e.target.value })}
            placeholder={t("optionalFallback")}
          />
        </div>
      )}
      {showTi && (
        <div className="field">
          <label>{t("nameTi")}</label>
          <input
            lang="ti"
            value={form.name_ti}
            onChange={(e) => setForm({ ...form, name_ti: e.target.value })}
            placeholder={t("optionalFallback")}
          />
        </div>
      )}
      {showNl && (
        <div className="field">
          <label>{t("nameNl")}</label>
          <input
            lang="nl"
            value={form.name_nl}
            onChange={(e) => setForm({ ...form, name_nl: e.target.value })}
            placeholder="bijv. Koffie verkeerd"
          />
        </div>
      )}
      <div className="field">
        <label>{t("category")}</label>
        <input
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          placeholder={t("categoryPlaceholder")}
        />
      </div>
      {showStation && (
        <div className="field">
          <label>{t("station")}</label>
          <select value={form.station || ""} onChange={(e) => setForm({ ...form, station: e.target.value })}>
            <option value="">{t("station_auto")}</option>
            <option value="kitchen">🍳 Kitchen</option>
            <option value="bar">🍺 Bar</option>
            <option value="cafe">☕ Café</option>
          </select>
        </div>
      )}
      <div className="field">
        <label>{t("price")}</label>
        <input
          type="number"
          step="0.01"
          value={form.price}
          onChange={(e) => setForm({ ...form, price: e.target.value })}
          required
        />
      </div>
      <div className="field">
        <label>{t("cost")}</label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.cost || ""}
          onChange={(e) => setForm({ ...form, cost: e.target.value })}
          placeholder="—"
        />
        <div className="muted" style={{ fontSize: "0.8rem", marginTop: 2 }}>{t("costHint")}</div>
      </div>
      <div className="field">
        <label>{t("description")}</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
      {/* Photo picker. Replaces the old "Image URL" text input —
          the manager picks a file from their computer/phone and
          we resize it client-side if needed, then upload after the
          item is saved. */}
      <div className="field">
        <label>{t("photo")}</label>
        <input
          ref={formImageInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: "none" }}
          onChange={async (e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (!f) return;
            try {
              const resized = await resizeImage(f);
              if (pendingImage?.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
              setPendingImage({
                file: resized,
                previewUrl: URL.createObjectURL(resized),
              });
              setRemoveExistingImage(false);
            } catch (err) {
              setError(err?.message || t("errImage"));
            }
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {pendingImage ? (
            <img
              src={pendingImage.previewUrl}
              alt="preview"
              style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8 }}
            />
          ) : form.image_url && !removeExistingImage ? (
            <img
              src={resolveImageUrl(form.image_url)}
              alt="current"
              style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8 }}
            />
          ) : (
            <div
              style={{
                width: 80, height: 80, borderRadius: 8,
                background: "#f0ece6",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#b9b2a4", fontSize: 24,
              }}
            >
              📷
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <button
              type="button"
              className="btn secondary small"
              onClick={() => formImageInputRef.current?.click()}
            >
              {pendingImage || (form.image_url && !removeExistingImage)
                ? t("changePhoto")
                : t("choosePhoto")}
            </button>
            {(pendingImage || (form.image_url && !removeExistingImage)) && (
              <button
                type="button"
                className="btn danger small"
                onClick={() => {
                  if (pendingImage?.previewUrl) {
                    URL.revokeObjectURL(pendingImage.previewUrl);
                  }
                  setPendingImage(null);
                  if (form.image_url) setRemoveExistingImage(true);
                  if (formImageInputRef.current) formImageInputRef.current.value = "";
                }}
              >
                {t("remove")}
              </button>
            )}
          </div>
        </div>
        <p
          className="muted"
          style={{ fontSize: "0.78rem", marginTop: 6, marginBottom: 0 }}
        >
          {t("photoHint")}
        </p>
      </div>
      {hasFeature("recipe_field") && (
        <div className="field">
          <label>{t("recipe")}</label>
          <textarea
            rows={4}
            value={form.recipe}
            onChange={(e) => setForm({ ...form, recipe: e.target.value })}
            placeholder="e.g.\n- 1 chicken breast\n- berbere\n- niter kibbeh\n- onion"
          />
        </div>
      )}
      <div className="field">
        <label>{t("modifiers")}</label>
        <textarea
          rows={4}
          value={form.modifiers_text}
          onChange={(e) => setForm({ ...form, modifiers_text: e.target.value })}
          placeholder={"No onion\nExtra cheese|1.5\nSpicy"}
        />
      </div>
      {/* Dietary / allergen tags. Renders as chips with checkboxes
          so the manager doesn't have to type slugs. Customer guests
          see these on the menu page and can filter. */}
      <div className="field">
        <label>{t("dietaryTags")}</label>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
          }}
        >
          {DIETARY_TAG_OPTIONS.map((opt) => {
            const active = (form.dietary_tags || []).includes(opt.slug);
            return (
              <label
                key={opt.slug}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px",
                  borderRadius: 999,
                  cursor: "pointer",
                  background: active ? opt.color : "#f5f1ea",
                  color: active ? "#fff" : "#1f2933",
                  border: active
                    ? `1px solid ${opt.color}`
                    : "1px solid #d3c8af",
                  fontSize: "0.82rem",
                  fontWeight: 600,
                }}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) =>
                    setForm((f) => {
                      const cur = new Set(f.dietary_tags || []);
                      if (e.target.checked) cur.add(opt.slug);
                      else cur.delete(opt.slug);
                      return { ...f, dietary_tags: Array.from(cur) };
                    })
                  }
                  style={{ display: "none" }}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
        <p
          className="muted"
          style={{ marginTop: 4, fontSize: "0.78rem" }}
        >
          {t("dietaryHint")}
        </p>
      </div>
      <label style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
        <input
          type="checkbox"
          checked={form.available}
          onChange={(e) => setForm({ ...form, available: e.target.checked })}
        />
        {t("available")}
      </label>

      {/* CHEF PICK — bubbles this item to the "Recommended" strip at
          the top of the customer menu, before auto top-sellers. Both
          chef and manager can toggle it; the optional note appears as
          a small caption under the item ("Chef's specialty"). */}
      <div
        style={{
          marginTop: 14, padding: "12px 14px",
          border: "1px solid #f0d38b", background: "#fffaf0",
          borderRadius: 8,
        }}
      >
        <label
          style={{ display: "inline-flex", gap: 8, alignItems: "center",
                   fontWeight: 600 }}
        >
          <input
            type="checkbox"
            checked={!!form.is_chef_pick}
            onChange={(e) =>
              setForm({ ...form, is_chef_pick: e.target.checked })
            }
          />
          {t("chefPick")}
        </label>
        {form.is_chef_pick && (
          <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
            <label style={{ fontSize: ".85rem" }}>
              {t("caption")}
            </label>
            <input
              type="text"
              value={form.chef_pick_note}
              onChange={(e) =>
                setForm({ ...form, chef_pick_note: e.target.value })
              }
              placeholder={t("captionPlaceholder")}
              maxLength={80}
            />
          </div>
        )}
      </div>

      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
        <button className="btn" type="submit">
          {editingId ? t("save") : t("add")}
        </button>
        {editingId && (
          <button type="button" className="btn secondary" onClick={onCancel}>
            {t("cancel")}
          </button>
        )}
      </div>
    </form>
  );
}
